import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Button, Form } from 'react-bootstrap';
import { ChevronDown, ExclamationTriangleFill, Search, XLg } from 'react-bootstrap-icons';
import { ContractTag, InactiveTagBadge } from '@/components/clientFlags/ClientTags';
import { formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import { useCurrency } from './currencyContext';
import type { ClientSidebarProps, SidebarStack, SidebarView } from './fleetData';
import { fmtUSD0 } from './shared';
import './ClientSidebar.css';

type Kind = SidebarStack['kind'];
type SortKey = 'spend' | 'name' | 'updated';

const VIEW_GROUPS: ReadonlyArray<{ id: SidebarView['group']; label: string }> = [
  { id: 'customers', label: 'Customers' },
  { id: 'partners', label: 'Nolia and partners' },
  { id: 'arcanum', label: 'Arcanum' },
];

const KINDS: ReadonlyArray<{ id: Kind; label: string; hint: string; empty: string; tag: string }> = [
  { id: 'customer', label: 'Customers', hint: 'Stacks that customers use.', empty: 'No customer stacks.', tag: '' },
  {
    id: 'dev',
    label: 'Dev',
    hint: "Arcanum's development, demo and test stacks. No customer uses them.",
    empty: 'No dev stacks.',
    tag: 'Dev',
  },
  { id: 'internal', label: 'Internal', hint: "Arcanum's own Numa.", empty: 'No internal stacks.', tag: 'Internal' },
  {
    id: 'infra',
    label: 'Infra',
    hint: 'Arcanum AWS accounts that run no stack: build, deploy and shared services.',
    empty: 'No infrastructure accounts.',
    tag: 'Infrastructure account',
  },
];

const SORTS: ReadonlyArray<{ id: SortKey; label: string }> = [
  { id: 'spend', label: 'Spend, highest first' },
  { id: 'name', label: 'Name, A to Z' },
  { id: 'updated', label: 'Last updated, oldest first' },
];

const SKELETON_ROWS = 8;
const wholeNumber = new Intl.NumberFormat('en-NZ');

const plural = (n: number, one: string, many: string) => `${wholeNumber.format(n)} ${n === 1 ? one : many}`;

const byLabel = (a: SidebarStack, b: SidebarStack) =>
  a.label.localeCompare(b.label, 'en', { sensitivity: 'base', numeric: true });

/** A stack with no update on record sorts as the oldest. */
const updatedAt = (stack: SidebarStack) => {
  const at = stack.generatedAt ? Date.parse(stack.generatedAt) : NaN;
  return Number.isFinite(at) ? at : 0;
};

const COMPARE: Record<SortKey, (a: SidebarStack, b: SidebarStack) => number> = {
  spend: (a, b) => (b.spendUsd ?? -1) - (a.spendUsd ?? -1) || byLabel(a, b),
  name: byLabel,
  updated: (a, b) => updatedAt(a) - updatedAt(b) || byLabel(a, b),
};

/** "40 minutes ago", "28 days ago": the age of a snapshot, in whole words for a sentence. */
function agoInWords(iso: string | undefined): string {
  const then = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(then)) return '';
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${plural(minutes, 'minute', 'minutes')} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${plural(hours, 'hour', 'hours')} ago`;
  return `${plural(Math.round(hours / 24), 'day', 'days')} ago`;
}

/** What the warning mark says: how old the numbers are, and why when the last update failed. */
function staleNote(stack: SidebarStack): string {
  const ago = agoInWords(stack.generatedAt);
  const when = ago ? `Last updated ${ago} (${formatNzDateTime(stack.generatedAt)}).` : 'No update on record.';
  if (!stack.failed) return when;
  return stack.error ? `${when} The last update failed: ${stack.error}` : `${when} The last update failed.`;
}

/** The quiet line under a stack's name. Empty when there is nothing worth a line. */
function stackDetail(stack: SidebarStack, sort: SortKey, searching: boolean): string {
  const parts: string[] = [];
  // A search looks through every type of stack, so a match says which type it is.
  const tag = searching ? KINDS.find((k) => k.id === stack.kind)?.tag : '';
  if (tag) parts.push(tag);
  if (sort === 'updated') {
    // The list is in order of age, so each row shows the age it is sorted by.
    const ago = timeAgo(stack.generatedAt);
    parts.push(ago ? `Updated ${ago}` : 'No update on record');
  } else if (stack.conversations !== null) {
    parts.push(plural(stack.conversations, 'conversation', 'conversations'));
  }
  return parts.join(' · ');
}

function Placeholder({ shape }: { shape: 'count' | 'spend' | 'label' | 'detail' }) {
  return <span className={`client-sidebar__skeleton client-sidebar__skeleton--${shape}`} aria-hidden="true" />;
}

interface ViewRowProps {
  view: SidebarView;
  loading: boolean;
  selected: boolean;
  onSelect: (id: string) => void;
}

function ViewRow({ view, loading, selected, onSelect }: ViewRowProps) {
  const unit =
    view.unit === 'accounts' ? plural(view.count, 'account', 'accounts') : plural(view.count, 'stack', 'stacks');
  return (
    <button
      type="button"
      data-nav
      className={selected ? 'client-sidebar__view is-selected' : 'client-sidebar__view'}
      aria-current={selected ? 'true' : undefined}
      // The selected view spells its description out below. The others give it on hover.
      title={selected ? undefined : view.description}
      onClick={() => onSelect(view.id)}
    >
      <span className="client-sidebar__view-top">
        <span className="client-sidebar__view-label">{view.label}</span>{' '}
        <span className="client-sidebar__view-count numeric">{loading ? <Placeholder shape="count" /> : unit}</span>
      </span>{' '}
      <span className="client-sidebar__view-desc">{view.description}</span>
    </button>
  );
}

interface StackRowProps {
  stack: SidebarStack;
  selected: boolean;
  detail: string;
  onSelect: (id: string) => void;
}

function StackRow({ stack, selected, detail, onSelect }: StackRowProps) {
  const warning = stack.stale || stack.failed ? staleNote(stack) : '';
  const inactive = stack.inactiveTag ?? (stack.inactive ? { setBy: '', setAt: '' } : undefined);
  const tagged = Boolean(stack.contract || inactive);
  return (
    <li>
      <button
        type="button"
        data-nav
        className={selected ? 'client-sidebar__stack is-selected' : 'client-sidebar__stack'}
        aria-current={selected ? 'true' : undefined}
        onClick={() => onSelect(stack.name)}
      >
        <span className="client-sidebar__stack-name">
          <span className="client-sidebar__stack-label" title={stack.label}>
            {stack.label}
          </span>{' '}
          {warning && (
            <span className="client-sidebar__warn" role="img" aria-label={warning} title={warning}>
              <ExclamationTriangleFill aria-hidden="true" />
            </span>
          )}
          {tagged && (
            <span className={`client-tags client-sidebar__tags${selected ? ' client-tags--on-selected' : ''}`}>
              {stack.contract && <ContractTag {...stack.contract} />}
              {inactive && <InactiveTagBadge tag={inactive} />}
            </span>
          )}
        </span>{' '}
        <span className="client-sidebar__stack-spend numeric">
          {stack.spendUsd === null ? <Placeholder shape="spend" /> : fmtUSD0(stack.spendUsd)}
        </span>{' '}
        {detail && <span className="client-sidebar__stack-detail">{detail}</span>}
      </button>
    </li>
  );
}

/**
 * The dashboard's left column. The fleet views sit on top: each one sums a
 * named set of stacks. Under them is every stack with its spend in the
 * selected window, to find by name, filter by type and sort. The page works
 * out every number. This column draws, filters and sorts.
 */
export function ClientSidebar({
  loading,
  views,
  stacks,
  selected,
  onSelect,
  windowLabel,
  inactiveCount,
  showInactive,
  onShowInactive,
}: ClientSidebarProps) {
  // Money follows the currency control. Reading it here redraws the rows when it changes.
  useCurrency();

  const labelId = useId();
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<Kind>('customer');
  const [sort, setSort] = useState<SortKey>('spend');
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // A stack picked elsewhere on the page can sit under another type filter.
  // The filter follows it once, so the selected row is in the list. After that
  // the person is free to change the filter.
  const selectedStack = stacks.find((stack) => stack.name === selected);
  const toFollow = selectedStack ? selectedStack.name : null;
  const [followed, setFollowed] = useState<string | null>(null);
  if (toFollow !== followed) {
    setFollowed(toFollow);
    if (selectedStack && selectedStack.kind !== kind) setKind(selectedStack.kind);
  }

  const counts = useMemo(() => {
    const out: Record<Kind, number> = { customer: 0, dev: 0, internal: 0, infra: 0 };
    for (const stack of stacks) out[stack.kind] += 1;
    return out;
  }, [stacks]);

  const needle = query.trim().toLowerCase();
  const searching = needle !== '';
  const visible = useMemo(() => {
    const rows = searching
      ? stacks.filter((stack) => stack.label.toLowerCase().includes(needle))
      : stacks.filter((stack) => stack.kind === kind);
    return rows.slice().sort(COMPARE[sort]);
  }, [stacks, searching, needle, kind, sort]);
  const hasRows = visible.length > 0;

  // A new filter, sort or search starts the list at its top.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [kind, sort, needle]);

  // Bring the selected stack into view. Only the list moves, never the page.
  useEffect(() => {
    const list = listRef.current;
    const row = list?.querySelector<HTMLElement>('[aria-current="true"]');
    if (!list || !row) return;
    const frame = list.getBoundingClientRect();
    const at = row.getBoundingClientRect();
    if (at.top < frame.top) list.scrollTop -= frame.top - at.top;
    else if (at.bottom > frame.bottom) list.scrollTop += at.bottom - frame.bottom;
  }, [selected, kind, hasRows]);

  const clearSearch = () => {
    setQuery('');
    searchRef.current?.focus();
  };

  const onSearchKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape' && query) {
      event.preventDefault();
      setQuery('');
    } else if (event.key === 'Enter' && searching && visible[0]) {
      // Enter opens the first match.
      event.preventDefault();
      onSelect(visible[0].name);
    }
  };

  // Up and down arrows walk the views and the stacks. Down from the search box lands on the first stack.
  const onArrowKey = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const from = event.target as HTMLElement;
    const down = event.key === 'ArrowDown';
    let next: HTMLElement | null | undefined;
    if (from === searchRef.current) {
      next = down ? listRef.current?.querySelector<HTMLElement>('[data-nav]') : null;
    } else if (from.matches('[data-nav]')) {
      const rows = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[data-nav]'));
      next = rows[rows.indexOf(from) + (down ? 1 : -1)];
    }
    if (!next) return;
    event.preventDefault();
    next.focus();
  };

  const matchText = hasRows
    ? `${plural(visible.length, 'match', 'matches')} in all stacks`
    : loading
      ? 'Loading stacks'
      : 'No matches';

  return (
    <nav className="client-sidebar" aria-label="Fleet views and stacks" aria-busy={loading} onKeyDown={onArrowKey}>
      <div className="client-sidebar__search">
        <div className="client-sidebar__search-field">
          <Search className="client-sidebar__search-icon" aria-hidden="true" />
          <Form.Control
            ref={searchRef}
            type="search"
            size="sm"
            className="client-sidebar__search-input"
            placeholder="Find a client"
            aria-label="Find a client"
            autoComplete="off"
            spellCheck={false}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onSearchKey}
          />
          {query && (
            <button
              type="button"
              className="client-sidebar__search-clear"
              aria-label="Clear the search"
              onClick={clearSearch}
            >
              <XLg aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      <div className="client-sidebar__views">
        {VIEW_GROUPS.map((group) => {
          const inGroup = views.filter((view) => view.group === group.id);
          if (inGroup.length === 0) return null;
          const id = `${labelId}-${group.id}`;
          return (
            <div key={group.id} role="group" aria-labelledby={id}>
              <h2 id={id} className="section-label client-sidebar__label">
                {group.label}
              </h2>
              {inGroup.map((view) => (
                <ViewRow
                  key={view.id}
                  view={view}
                  loading={loading}
                  selected={view.id === selected}
                  onSelect={onSelect}
                />
              ))}
            </div>
          );
        })}
      </div>

      <div className="client-sidebar__stacks-head">
        <div className="client-sidebar__stacks-title">
          <h2 className="section-label client-sidebar__label">Stacks</h2>
          <span className="client-sidebar__window" title={`The numbers below cover: ${windowLabel}`}>
            {windowLabel}
          </span>
        </div>

        {searching ? (
          <p className="client-sidebar__matches" role="status">
            {matchText}
          </p>
        ) : (
          <div className="client-sidebar__kinds" role="group" aria-label="Type of stack">
            {KINDS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={option.id === kind ? 'client-sidebar__kind is-active' : 'client-sidebar__kind'}
                aria-pressed={option.id === kind}
                title={option.hint}
                onClick={() => setKind(option.id)}
              >
                <span className="client-sidebar__kind-label">{option.label}</span>{' '}
                <span className="client-sidebar__kind-count numeric">
                  {loading ? <Placeholder shape="count" /> : wholeNumber.format(counts[option.id])}
                </span>
              </button>
            ))}
          </div>
        )}

        <label className="client-sidebar__sort">
          <span className="client-sidebar__sort-label">Sort by</span>
          <span className="client-sidebar__sort-field">
            <select
              className="client-sidebar__sort-select"
              value={sort}
              onChange={(event) => setSort(event.target.value as SortKey)}
            >
              {SORTS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown className="client-sidebar__sort-caret" aria-hidden="true" />
          </span>
        </label>

        {inactiveCount > 0 && (
          <label
            className="client-sidebar__show-inactive"
            title="Inactive clients are left out of every list and every use and credit figure unless this is on. Their spend is always in the totals."
          >
            <input type="checkbox" checked={showInactive} onChange={(event) => onShowInactive(event.target.checked)} />
            <span>
              Show inactive <span className="numeric">({wholeNumber.format(inactiveCount)})</span>
            </span>
          </label>
        )}
      </div>

      <div className="client-sidebar__list" ref={listRef}>
        {hasRows && (
          <ul className="client-sidebar__rows">
            {visible.map((stack) => (
              <StackRow
                key={stack.name}
                stack={stack}
                selected={stack.name === selected}
                detail={stackDetail(stack, sort, searching)}
                onSelect={onSelect}
              />
            ))}
          </ul>
        )}

        {!hasRows && loading && (
          <div className="client-sidebar__ghosts">
            <span className="visually-hidden" role="status">
              Loading stacks
            </span>
            {Array.from({ length: SKELETON_ROWS }, (_, index) => (
              <div key={index} className="client-sidebar__ghost" aria-hidden="true">
                <Placeholder shape="label" />
                <Placeholder shape="spend" />
                <Placeholder shape="detail" />
              </div>
            ))}
          </div>
        )}

        {!hasRows && !loading && searching && (
          <div className="client-sidebar__empty">
            <p>{`No stack has "${query.trim()}" in its name.`}</p>
            <Button variant="outline-secondary" size="sm" onClick={clearSearch}>
              Clear the search
            </Button>
          </div>
        )}

        {!hasRows && !loading && !searching && (
          <div className="client-sidebar__empty">
            <p>{KINDS.find((option) => option.id === kind)?.empty}</p>
          </div>
        )}
      </div>
    </nav>
  );
}
